import {
    Anchor,
    Badge,
    Box,
    Button,
    Flex,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core'
import { LiteProductVariant, ProductConditions } from '@ortros/stoux-lib'
import {
    Trash2,
} from 'lucide-react'
import { motion } from 'motion/react'

import {
    PRODUCT_CONDITIONS_COLOR,
    PRODUCT_CONDITIONS_WORD,
} from '@/modules/ProductTemplate/shared/constants'
import { fadeIn } from '@/utils/animations'
import formatAmount from '@/utils/format-amount'
import Image from 'next/image'
import ImageWithFallback from '@/components/ui/ImageWithFallback'

type Props = {
    dataSource: LiteProductVariant & { template_barcode: string }
    onRemove?: () => void
    readonly?: boolean
}

const ListLineItem = ({ dataSource, onRemove, readonly }: Props) => {
    return (
        <motion.div
            role="listitem"
            aria-label="Cart Line Item"
            aria-level={3}
            data-testid="cart-line-item"
            className="relative flex flex-col items-start justify-start w-full text-left"
            {...fadeIn}
        >
            <Flex className="w-full gap-x-4">
                <Box className="flex items-center justify-center">
                    <ImageWithFallback
                        src={
                            dataSource.template_thumbnail ??
                            '/images/products/placeholder.png'
                        }
                        alt={dataSource.template_title ?? 'Product Image'}
                        width={80}
                        height={80}
                        className="object-cover rounded w-18 h-18 aspect-square"
                    />
                </Box>
                <Stack
                    role="contentinfo"
                    spacing={4}
                    className="w-full max-w-md overflow-hidden text-left"
                >
                    <Text
                        color="gray.8"
                        className="max-w-[80%] capitalize lg:max-w-[90%] xl:max-w-[16vw] 2xl:max-w-[12vw] overflow-hidden text-sm text-left truncate text-ellipsis"
                    >
                        {dataSource.template_title}
                    </Text>

                    <Badge
                        w="max-content"
                        size="xs"
                        radius="xs"
                        variant="dot"
                        color={
                            PRODUCT_CONDITIONS_COLOR[
                                dataSource?.product_condition ||
                                    ProductConditions.VERY_GOOD
                            ]
                        }
                    >
                        {!!dataSource.product_condition &&
                            PRODUCT_CONDITIONS_WORD[
                                dataSource.product_condition
                            ]}
                    </Badge>

                    <div className="flex items-center justify-between w-full mt-4">
                        <Anchor href={`/p/${dataSource.template_barcode}`}>
                            <Button variant="subtle" color="gray" size="xs">
                                Voir le produit
                            </Button>
                        </Anchor>

                        <Text className="font-bold">
                            {!!dataSource.price_amount &&
                                formatAmount(dataSource.price_amount)}
                        </Text>
                    </div>
                </Stack>
            </Flex>
            {!readonly && (
                <Tooltip label={`Supprimer`}>
                    <Button
                        aria-label="Supprimer le produit du panier"
                        onClick={onRemove}
                        className="absolute top-0 right-0 bg-white"
                        size="xs"
                        mt="auto"
                        compact
                        ml="auto"
                        variant="subtle"
                        title="Supprimer"
                        color="gray.1"
                        c="gray.7"
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </Tooltip>
            )}
        </motion.div>
    )
}

ListLineItem.defaultProps = {
    readonly: false,
}

export default ListLineItem
